/**
 * Safely extracts a human-readable error message from any thrown value.
 *
 * Handles:
 * - Error instances (native JS errors)
 * - API error objects ({ message, status, details })
 * - Axios-style errors ({ response: { data: { message, detail } } })
 * - Plain objects with message/error/detail fields
 * - Strings
 * - Unknown types (fallback to JSON.stringify)
 */
export function extractErrorMessage(err: unknown, fallback = "An unexpected error occurred"): string {
  // Standard Error instance
  if (err instanceof Error) {
    return err.message;
  }

  // String thrown directly
  if (typeof err === "string") {
    return err;
  }

  // Object-shaped errors
  if (err && typeof err === "object") {
    const obj = err as Record<string, unknown>;

    // { message: "..." }
    if (typeof obj.message === "string" && obj.message) {
      return obj.message;
    }

    // { error: "..." }
    if (typeof obj.error === "string" && obj.error) {
      return obj.error;
    }

    // { detail: "..." } or { detail: { message: "..." } }
    if (obj.detail) {
      if (typeof obj.detail === "string") {
        return obj.detail;
      }
      if (typeof obj.detail === "object" && obj.detail !== null) {
        const detail = obj.detail as Record<string, unknown>;
        if (typeof detail.message === "string") return detail.message;
        if (typeof detail.error === "string") return detail.error;
      }
    }

    // Axios-style: { response: { data: { message, detail } } }
    if (obj.response && typeof obj.response === "object") {
      const resp = obj.response as Record<string, unknown>;
      if (resp.data && typeof resp.data === "object") {
        const data = resp.data as Record<string, unknown>;
        if (typeof data.message === "string") return data.message;
        if (typeof data.detail === "string") return data.detail;
      }
    }

    // Last resort: try JSON serialization (avoids [object Object])
    try {
      const json = JSON.stringify(obj);
      if (json && json !== "{}") {
        return json;
      }
    } catch {
      // circular reference or other issue — fall through
    }
  }

  return fallback;
}
